/**
 * DeleteTaskDialog (Story 28.2 + 28.3) — §4.4 confirmation Dialog (no
 * type-to-confirm: a task is a lightweight item). Same Dialog scaffold as
 * DeleteClientDialog per the design-system directive §4.
 *
 * 28.3: a RECURRING task (isRecurring || recurrenceParentId != null) offers two
 * destructive choices — "delete only this" (?scope=this) vs "delete this & all
 * future occurrences" (?scope=all). A non-recurring task keeps the single Delete.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@provenleap/ui";
import { Trash } from "@phosphor-icons/react";
import { apiDelete } from "../../lib/apiClient";
import type { ToastType } from "../../hooks/useToast";
import type { Task } from "./types";

type DeleteScope = "this" | "all";

interface DeleteTaskDialogProps {
  task: Task | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
  showToast: (type: ToastType, message: string) => void;
}

export function DeleteTaskDialog({
  task,
  open,
  onOpenChange,
  onDeleted,
  showToast,
}: DeleteTaskDialogProps) {
  const queryClient = useQueryClient();
  const isRecurring =
    !!task && (task.isRecurring || task.recurrenceParentId !== null);

  const deleteTask = useMutation({
    mutationFn: async (scope: DeleteScope) => {
      if (!task) throw new Error("No task selected");
      const res = await apiDelete(`/api/tasks/${task.id}?scope=${scope}`);
      if (!res.ok && res.status !== 404) {
        const json = await res.json().catch(() => ({ detail: "Server error" }));
        throw { status: res.status, ...json };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      onDeleted();
    },
    onError: (err: any) => {
      showToast("error", err?.title ?? err?.detail ?? "Failed to delete task");
    },
  });

  const pending = deleteTask.isPending;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && task && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex flex-col space-y-3 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    Delete {task.title}?
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {isRecurring
                      ? "This is a recurring task. Delete only this occurrence, or this and all future occurrences? This action cannot be undone."
                      : "This task will be permanently removed. This action cannot be undone."}
                  </DialogPrimitive.Description>
                </div>

                {isRecurring ? (
                  <div className="flex flex-col gap-2 mt-6">
                    <Button
                      variant="outline"
                      className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                      style={{ border: "0.5px solid var(--hairline)" }}
                      onClick={() => deleteTask.mutate("this")}
                      disabled={pending}
                      data-testid="confirm-delete-task-btn"
                    >
                      <Trash size={16} className="mr-1" />
                      Delete only this task
                    </Button>
                    <Button
                      variant="destructive"
                      className="rounded-2xl cursor-pointer"
                      onClick={() => deleteTask.mutate("all")}
                      disabled={pending}
                      data-testid="confirm-delete-task-all-btn"
                    >
                      <Trash size={16} className="mr-1" />
                      {pending ? "Deleting..." : "Delete this & all future occurrences"}
                    </Button>
                    <Button
                      variant="ghost"
                      className="rounded-2xl cursor-pointer hover:bg-accent transition-all"
                      onClick={() => onOpenChange(false)}
                      disabled={pending}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
                    <Button
                      variant="ghost"
                      className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                      style={{ border: "0.5px solid hsl(var(--input))" }}
                      onClick={() => onOpenChange(false)}
                      disabled={pending}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      className="rounded-2xl cursor-pointer"
                      onClick={() => deleteTask.mutate("this")}
                      disabled={pending}
                      data-testid="confirm-delete-task-btn"
                    >
                      <Trash size={16} className="mr-1" />
                      {pending ? "Deleting..." : "Delete"}
                    </Button>
                  </div>
                )}
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
